import { observer } from 'mobx-react';
import React from 'react';
import { Badge } from '@components/ui/badge';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@components/ui/card';
import { Switch } from '@components/ui/switch';
import { toast } from '@core/toast';
import { UserContext } from '..';
import { useError } from '../../../components/hooks/useError';
import { Api } from '../../../core/api';
import { CatalogAppDto } from '../../../core/api/MobileClient';
import { injectTSDI } from '../../../core/tsdi';

export const UserAppAccessCard = observer(() => {
    const user = React.useContext(UserContext);
    const { mobileClient } = injectTSDI(Api);
    const showError = useError();
    const [catalogApps, setCatalogApps] = React.useState<CatalogAppDto[]>([]);
    const [grantedApps, setGrantedApps] = React.useState<string[]>([]);
    const [busy, setBusy] = React.useState(false);

    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        try {
            await action();
            const [catalog, access] = await Promise.all([
                mobileClient.supportApi.catalogAppSupportControllerGetCatalogApps(),
                mobileClient.supportApi.userAppSupportControllerGetUserAppAccess(
                    user.id
                )
            ]);
            setCatalogApps(catalog.data);
            setGrantedApps(access.data);
        } catch (e) {
            showError(e);
        } finally {
            setBusy(false);
        }
    };

    React.useEffect(() => {
        void run(async () => undefined);
    }, [user.id]);

    const setAccess = (app: string, granted: boolean) =>
        run(async () => {
            if (granted) {
                await mobileClient.supportApi.userAppSupportControllerGrantUserAppAccess(
                    user.id,
                    app
                );
            } else {
                await mobileClient.supportApi.userAppSupportControllerRevokeUserAppAccess(
                    user.id,
                    app
                );
            }
            toast.success(
                `Access to ${app} ${granted ? 'granted' : 'revoked'}`
            );
        });

    const exceptionApps = catalogApps.filter(
        (catalogApp) =>
            catalogApp.listed &&
            (!catalogApp.enabled || grantedApps.includes(catalogApp.app))
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle>App store access</CardTitle>
                <CardDescription>
                    Apps disabled in the App Store are hidden from everyone.
                    Granting access shows a disabled app in this user's store
                    anyway.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {exceptionApps.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        Every app is enabled in the App Store.
                    </p>
                ) : (
                    <div className="flex flex-col divide-y divide-border">
                        {exceptionApps.map((catalogApp) => (
                            <div
                                key={catalogApp.app}
                                className="flex items-center justify-between gap-3 py-3"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-sm font-semibold">
                                        {catalogApp.app}
                                    </span>
                                    {catalogApp.enabled ? (
                                        <Badge variant="success">
                                            Enabled for everyone
                                        </Badge>
                                    ) : (
                                        <Badge variant="secondary">
                                            Disabled globally
                                        </Badge>
                                    )}
                                </div>
                                <Switch
                                    checked={grantedApps.includes(
                                        catalogApp.app
                                    )}
                                    disabled={busy}
                                    onCheckedChange={(granted: boolean) =>
                                        void setAccess(catalogApp.app, granted)
                                    }
                                />
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
});
